import {
    Alert,
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TableCell,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SalaryDataTable from './SalaryDataTable';
import SalaryFormSection from './SalaryFormSection';
import useRequest from '../../hooks/useRequest';
import type { UserPenalty } from '../../domain/salary/userPenalties';

const today = new Date().toISOString().slice(0, 10);

const emptyForm = {
    utilizator: '',
    diff: '',
    mentiuni: '',
    seAplica: true,
    dataAbatere: today,
};

const PenaltiesTab = () => {
    const { t } = useTranslation();
    const request = useRequest();
    const [penalties, setPenalties] = useState<UserPenalty[]>([]);
    const [form, setForm] = useState(emptyForm);
    const [error, setError] = useState<string | null>(null);

    const [selectedUserFilter, setSelectedUserFilter] = useState<string>('');
    const [selectedPeriodFilter, setSelectedPeriodFilter] = useState<string>(today.slice(0, 7)); // YYYY-MM

    const load = useCallback(async () => {
        try {
            // First, trigger a sync for the currently selected period (or a wide range if empty)
            let start, end;
            if (selectedPeriodFilter) {
                start = `${selectedPeriodFilter}-01`;
                const [year, month] = selectedPeriodFilter.split('-').map(Number);
                const lastDay = new Date(year, month, 0).getDate();
                end = `${selectedPeriodFilter}-${lastDay}`;
            } else {
                start = '2000-01-01';
                end = '2100-01-01';
            }

            try {
                await request({
                    path: '/user-penalties/sync',
                    method: 'POST',
                    params: { startDate: start, endDate: end },
                });
            } catch (e) {
                console.warn('Sync user penalties failed', e);
            }

            const data = await request<UserPenalty[]>({ path: '/user-penalties' });
            setPenalties(data);
        } catch {
            setError(t('salary.penalties.loadError'));
        }
    }, [request, t, selectedPeriodFilter]);

    useEffect(() => {
        void load();
    }, [load]);

    const handleAdd = async () => {
        const diffValue = parseFloat(form.diff);
        if (!form.utilizator.trim() || isNaN(diffValue)) return;
        try {
            await request({
                path: '/user-penalties',
                method: 'POST',
                params: {
                    utilizator: form.utilizator.trim(),
                    diff: diffValue,
                    mentiuni: form.mentiuni.trim() || null,
                    seAplica: form.seAplica,
                    dataAbatere: form.dataAbatere || null,
                },
            });
            setForm(emptyForm);
            await load();
        } catch {
            setError(t('salary.penalties.saveError'));
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await request({ path: `/user-penalties/${id}`, method: 'DELETE' });
            await load();
        } catch {
            setError(t('salary.penalties.deleteError'));
        }
    };

    const handleToggle = async (penalty: UserPenalty, field: 'seAplica' | 'aplicat') => {
        try {
            await request({
                path: '/user-penalties',
                method: 'POST',
                params: {
                    ...penalty,
                    [field]: !penalty[field],
                    ...(field === 'aplicat' && {
                        dataAplicare: !penalty.aplicat
                            ? new Date().toISOString().slice(0, 10)
                            : null,
                    }),
                },
            });
            await load();
        } catch {
            setError(t('salary.penalties.saveError'));
        }
    };

    const uniqueUsers = Array.from(
        new Set(penalties.map(p => p.utilizator).filter(Boolean)),
    ) as string[];

    const displayedPenalties = penalties.filter(p => {
        if (selectedUserFilter && p.utilizator !== selectedUserFilter) return false;
        if (selectedPeriodFilter && !p.dataAbatere?.startsWith(selectedPeriodFilter)) return false;
        return true;
    });

    const summaryTotal = displayedPenalties
        .filter(p => p.aplicat)
        .reduce((sum, p) => sum + (p.diff ?? 0), 0);

    let summaryTotalColor = 'text.primary';
    if (summaryTotal < 0) summaryTotalColor = 'success.main';
    else if (summaryTotal > 0) summaryTotalColor = 'error.main';

    return (
        <Stack spacing={2}>
            {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <SalaryFormSection
                title={t('salary.penalties.addTitle')}
                onAdd={handleAdd}
                addLabel={t('common.add')}
            >
                <TextField
                    size="small"
                    label={t('salary.penalties.employee')}
                    value={form.utilizator}
                    onChange={e => setForm({ ...form, utilizator: e.target.value })}
                    sx={{ minWidth: 140 }}
                />
                <TextField
                    size="small"
                    type="number"
                    label={t('salary.penalties.amount')}
                    value={form.diff}
                    onChange={e => setForm({ ...form, diff: e.target.value })}
                    sx={{ width: 110 }}
                    slotProps={{ htmlInput: { step: '0.01' } }}
                />
                <TextField
                    size="small"
                    label={t('salary.penalties.notes')}
                    value={form.mentiuni}
                    onChange={e => setForm({ ...form, mentiuni: e.target.value })}
                    sx={{ minWidth: 180 }}
                />
                <TextField
                    size="small"
                    type="date"
                    label={t('salary.penalties.date')}
                    value={form.dataAbatere}
                    onChange={e => setForm({ ...form, dataAbatere: e.target.value })}
                    slotProps={{ inputLabel: { shrink: true } }}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={form.seAplica}
                            onChange={e => setForm({ ...form, seAplica: e.target.checked })}
                            size="small"
                        />
                    }
                    label={t('salary.penalties.applies')}
                />
            </SalaryFormSection>

            <Paper sx={{ p: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                    <Typography variant="subtitle1" fontWeight="bold">
                        {t('salary.penalties.summary') || 'Summary'}:
                    </Typography>

                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>{t('salary.penalties.employee')}</InputLabel>
                        <Select
                            value={selectedUserFilter}
                            label={t('salary.penalties.employee')}
                            onChange={e => setSelectedUserFilter(e.target.value)}
                        >
                            <MenuItem value="">
                                <em>{t('common.all') || 'All'}</em>
                            </MenuItem>
                            {uniqueUsers.map(u => (
                                <MenuItem key={u} value={u}>
                                    {u}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        size="small"
                        type="month"
                        label={t('salary.penalties.period') || 'Period'}
                        value={selectedPeriodFilter}
                        onChange={e => setSelectedPeriodFilter(e.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }}
                    />

                    <Box flexGrow={1} />

                    <Typography variant="subtitle1" fontWeight="bold">
                        {t('salary.penalties.totalApplied') || 'Total Applied'}:
                    </Typography>
                    <Typography variant="h6" color={summaryTotalColor}>
                        {summaryTotal.toFixed(0)} {t('salary.currency')}
                    </Typography>
                </Stack>
            </Paper>

            <SalaryDataTable
                headers={
                    <>
                        <TableCell>{t('salary.penalties.employee')}</TableCell>
                        <TableCell align="right">{t('salary.penalties.amount')}</TableCell>
                        <TableCell>{t('salary.penalties.notes')}</TableCell>
                        <TableCell>{t('salary.penalties.date')}</TableCell>
                        <TableCell align="center">{t('salary.penalties.applies')}</TableCell>
                        <TableCell align="center">{t('salary.penalties.applied')}</TableCell>
                        <TableCell>{t('salary.penalties.appliedDate')}</TableCell>
                        <TableCell align="right"></TableCell>
                    </>
                }
            >
                {displayedPenalties.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={8} align="center">
                            <Box py={2} color="text.secondary">
                                {t('salary.penalties.noData')}
                            </Box>
                        </TableCell>
                    </TableRow>
                )}
                {displayedPenalties.map(penalty => (
                    <TableRow key={penalty.id}>
                        <TableCell>{penalty.utilizator ?? '-'}</TableCell>
                        <TableCell
                            align="right"
                            sx={{
                                color: (penalty.diff ?? 0) < 0 ? 'success.main' : 'error.main',
                                fontWeight: 600,
                            }}
                        >
                            {(penalty.diff ?? 0).toFixed(0)}
                        </TableCell>
                        <TableCell>{penalty.mentiuni ?? '-'}</TableCell>
                        <TableCell>
                            {penalty.dataAbatere ? penalty.dataAbatere.slice(0, 10) : '-'}
                        </TableCell>
                        <TableCell align="center">
                            <Checkbox
                                checked={penalty.seAplica}
                                onChange={() => handleToggle(penalty, 'seAplica')}
                                size="small"
                                color="primary"
                            />
                        </TableCell>
                        <TableCell align="center">
                            <Checkbox
                                checked={penalty.aplicat}
                                onChange={() => handleToggle(penalty, 'aplicat')}
                                size="small"
                                color="secondary"
                            />
                        </TableCell>
                        <TableCell>
                            {penalty.dataAplicare ? penalty.dataAplicare.slice(0, 10) : '-'}
                        </TableCell>
                        <TableCell align="right">
                            <Tooltip title={t('common.delete')}>
                                <IconButton size="small" onClick={() => handleDelete(penalty.id)}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </TableCell>
                    </TableRow>
                ))}
            </SalaryDataTable>
        </Stack>
    );
};

export default PenaltiesTab;
